import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ServiceConfigService } from '../service-config.service';
import { firstValueFrom } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class GlobalTranslateService {
  private translateApiUrl = 'rest/api/amazon/translate';

  constructor(
    private serverConfig: ServiceConfigService,
    private http: HttpClient
  ) {}

  private preprocessText(text: string): string {
    let customGlossaryList : string[] = [];
    const customGlossary = this.serverConfig.getServerConfig().globalTranslateCustomWordsList;
    if (customGlossary) {
      customGlossaryList.push(...customGlossary.split(','));
      Object.values(customGlossaryList).forEach((word, index) => {
        const placeHolder = `{{CUSTOM_${index}}}`
        text = text.replace(new RegExp(`\\b${word}\\b`, 'g'), placeHolder)
      });
    }
    return text;
  }

  private postProcessText(text: string): string {
    let customGlossaryList : string[] = [];
    const customGlossary = this.serverConfig.getServerConfig().globalTranslateCustomWordsList;
    if (customGlossary) {
      customGlossaryList.push(...customGlossary.split(','));
      Object.values(customGlossaryList).forEach((word, index) => {
        // Handle multiple possible placeholder formats that AWS might return
        const placeHolders = [
          `{{CUSTOM_${index}}}`,
          `{{ CUSTOM_${index} }}`,
          `{{ CUSTOM_${index}}}`,
          `{{CUSTOM_${index} }}`,
          `{ {CUSTOM_${index}} }`,
          `{CUSTOM_${index}}`,
          `{ {CUSTOM_${index}}}`,
          `{ {CUSTOM_${index}}`,
          `{{TUỲ CHỈNH_${index}}}`, `{{ TUỲ CHỈNH_${index} }}`, `{{ TUỲ CHỈNH_${index}}}`, `{{TUỲ CHỈNH_${index} }}`, `{ {TUỲ CHỈNH_${index}} }`, `{TUỲ CHỈNH_${index}}`, `{ {TUỲ CHỈNH_${index}}}`, `{ {TUỲ CHỈNH_${index}}`,
          `{{TÙY CHỈNH_${index}}}`, `{{ TÙY CHỈNH_${index} }}`, `{{ TÙY CHỈNH_${index}}}`, `{{TÙY CHỈNH_${index} }}`, `{ {TÙY CHỈNH_${index}} }`, `{TÙY CHỈNH_${index}}`, `{ {TÙY CHỈNH_${index}}}`, `{ {TÙY CHỈNH_${index}}`,
          `{{커스텀_${index}}}`, `{{ 커스텀_${index} }}`, `{{ 커스텀_${index}}}`, `{{커스텀_${index} }}`, `{ {커스텀_${index}} }`, `{커스텀_${index}}`, `{ {커스텀_${index}}}`, `{ {커스텀_${index}}`,
          `{{PERSONNALISÉ_${index}}}`, `{{ PERSONNALISÉ_${index} }}`, `{{ PERSONNALISÉ_${index}}}`, `{{PERSONNALISÉ_${index} }}`, `{ {PERSONNALISÉ_${index}} }`, `{PERSONNALISÉ_${index}}`, `{ {PERSONNALISÉ_${index}}}`, `{ {PERSONNALISÉ_${index}}`,
          `{{PERSONNALIS_${index}}}`, `{{ PERSONNALIS_${index} }}`, `{{ PERSONNALIS_${index}}}`, `{{PERSONNALIS_${index} }}`, `{ {PERSONNALIS_${index}} }`, `{PERSONNALIS_${index}}`, `{ {PERSONNALIS_${index}}}`, `{ {PERSONNALIS_${index}}}`,
          `{{ብጁ ${index}}}`, `{{ ብጁ ${index} }}`, `{{ ብጁ_${index}}}`, `{{ብጁ_${index} }}`, `{ {ብጁ_${index}} }`, `{ብጁ_${index}}`, `{ {ብጁ_${index}}}`, `{ {ብጁ_${index}}`,
          `{{કસ્ટમ_${index}}}`, `{{ કસ્ટમ_${index} }}`, `{{ કસ્ટમ_${index}}}`, `{{કસ્ટમ_${index} }}`, `{ {કસ્ટમ_${index}} }`, `{કસ્ટમ_${index}}`, `{ {કસ્ટમ_${index}}}`, `{ {કસ્ટમ_${index}}`,
          `{{カスタム_${index}}}`, `{{ カスタム_${index} }}`, `{{ カスタム_${index}}}`, `{{カスタム_${index} }}`, `{ {カスタム_${index}} }`, `{カスタム_${index}}`, `{ {カスタム_${index}}}`, `{ {カスタム_${index}}`,
          `{{سفارشی_${index}}}`, `{{ سفارشی_${index} }}`, `{{ سفارشی_${index}}}`, `{{سفارشی_${index} }}`, `{ {سفارشی_${index}} }`, `{سفارشی_${index}}`, `{ {سفارشی_${index}}}`, `{ {سفارشی_${index}}`,
        ];
        
        // Try all possible variations
        placeHolders.forEach(placeHolder => {
          text = text.split(placeHolder).join(word);
        });
      });
    }
    
    // Remove any remaining curly braces (standalone or in pairs)
    text = text.replace(/\{\s*\}/g, ''); // Remove empty braces {}
    text = text.replace(/\{\s*/g, '');   // Remove opening braces
    text = text.replace(/\s*\}/g, '');   // Remove closing braces
    
    return text;
  }

  gatewayURL() {
    return this.serverConfig.getServerConfig()['gatewayURL'];
  }


  private async translateSegment(text: string, targetLanguage: string): Promise<string> {
    if (!text || text.trim() === '') {
      return text;
    }
    const preprocessText = this.preprocessText(text);
    const payload = {
      Text: preprocessText,
      SourceLanguageCode: 'en',
      TargetLanguageCode: targetLanguage
    };

    const response = await firstValueFrom(
      this.http.post<{ TranslatedText: string }>(`${this.gatewayURL()}${this.translateApiUrl}`, payload)
    );

    const translatedText = response.TranslatedText;    
    // If translation is null or empty, return the original text
    if (!translatedText || translatedText.trim() === '') {
      return text;
    }  
      
    return this.postProcessText(translatedText);
  }


  async translate(text: string, targetLanguage: string): Promise<string> {
    // Return original text if empty or contains only whitespace
    if (!text || text.trim() === '') {
      return text;
    }

    try {
      // If no HTML tags, translate directly
      if (!/<[^>]+>/.test(text)) {
        return await this.translateSegment(text, targetLanguage);
      }

      // if HTML tags exist in text, split into HTML tags and text segments
      const segments = text.split(/(<[^>]+>)/g).filter(s => s !== '');

      const skipTranslate: boolean[] = [];
      let noTranslateDepth = 0;

      for (const segment of segments) {
        if (/^<[^>]+>$/.test(segment)) {
          const isOpenTag = /^<[a-zA-Z]/.test(segment);
          const isCloseTag = /^<\//.test(segment);
          const hasTranslateNo = /translate\s*=\s*["']no["']/i.test(segment);
          
          if (isOpenTag && hasTranslateNo) {
            noTranslateDepth++;
          }
          else if (isCloseTag && noTranslateDepth > 0) {
            noTranslateDepth--;
          }
          skipTranslate.push(true);
        } else {
          skipTranslate.push(noTranslateDepth > 0);
        }
      }

      const translatedSegments = await Promise.all(
        segments.map(async (segment, i) => {
          if (/^<[^>]+>$/.test(segment) || skipTranslate[i]) {
            return segment;
          }
          return await this.translateSegment(segment, targetLanguage);
        })
      );

      return translatedSegments.join('');
    } catch (error) {
        return text;
    }
  }

  public async getSupportedLanguages() {
    try {
      const response = await firstValueFrom(
        this.http.post<{ Languages: Array<{ LanguageCode: string; LanguageName: string }> }>(
          this.translateApiUrl,
          { DisplayLanguageCode: 'en' }
        )
      );
      return response.Languages?.map((lang) => ({
        LanguageCode: lang.LanguageCode,
        LanguageName: lang.LanguageName
      })) || [];
    } catch (error) {
      console.error('Error fetching supported languages:', error);
      return [];
    }
  }
}